import {useCurrentRepository} from '@github-ui/current-repository'
import {noop} from '@github-ui/noop'
import {encodePart} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback, useMemo} from 'react'

import type {CommitComment, CommitPayload} from '../types/commit-types'

export type Result = {
  error: Error | null
  comment: CommitComment | null
}

export type EditResult = {
  error: Error | null
  updatedFields: Pick<CommitComment, 'body' | 'bodyVersion' | 'htmlBody'> | null
}

type UpdateCommentResponse = {
  source: string
  bodyVersion: string
  body: SafeHTMLString
}

export function useCommenting() {
  const repo = useCurrentRepository()
  const payload = useRoutePayload<CommitPayload>()
  const {commit} = payload
  const createUrl = `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}/commit_comment/create`
  const updateUrl = `/${encodePart(repo.ownerLogin)}/${encodePart(repo.name)}/commit_comment`

  const addComment = useCallback(
    async (markdown: string, path?: string, position?: string): Promise<Result> => {
      const formData = new FormData()

      formData.append('commit_id', commit.oid)
      formData.append('comment[body]', markdown)

      if (path && position) {
        formData.append('path', path)
        formData.append('position', position.toString())
      }

      const response = await verifiedFetch(createUrl, {
        method: 'POST',
        body: formData,
      })

      if (response.ok) {
        const comment: CommitComment = (await response.json()).comment

        return {
          error: null,
          comment,
        }
      } else {
        return {
          error: new Error('Failed to add comment'),
          comment: null,
        }
      }
    },
    [commit.oid, createUrl],
  )

  const editComment = useCallback(
    async (markdown: string, comment: Pick<CommitComment, 'id' | 'bodyVersion'>): Promise<EditResult> => {
      const formData = new FormData()

      formData.append('_method', 'put')
      formData.append('commit_comment[id]', comment.id.toString())
      formData.append('commit_comment[body]', markdown)
      formData.append('commit_comment[bodyVersion]', comment.bodyVersion)

      const response = await verifiedFetch(updateUrl, {
        method: 'POST',
        body: formData,
      })

      if (response.ok) {
        const json: UpdateCommentResponse = await response.json()

        return {
          error: null,
          updatedFields: {
            body: json.source,
            bodyVersion: json.bodyVersion,
            htmlBody: json.body,
          },
        }
      } else {
        return {
          error: new Error('Failed to edit comment'),
          updatedFields: null,
        }
      }
    },
    [updateUrl],
  )

  const deleteComment = noop

  return useMemo(() => {
    return {
      addComment,
      editComment,
      deleteComment,
    }
  }, [addComment, deleteComment, editComment])
}
